import { useCallback, useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useCustomer } from "@/context/CustomerAuthContext";
import capi from "@/lib/customerApi";
import { apiErr } from "@/lib/api";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Package, Home, Boxes, LogOut, Pencil, Mail, Phone, User, RefreshCw, Plus } from "lucide-react";
import { toast } from "sonner";
import { PickupOrderCard, PackersOrderCard, ThreePLCard } from "@/components/customer/OrderCards";

const POLL_MS = 15000;

export default function MyAccount() {
  const { customer } = useCustomer();
  if (customer === null) return <div className="min-h-screen grid place-items-center text-muted-foreground">Loading…</div>;
  if (!customer) return <Navigate to="/signin" replace state={{ from: "/my-account" }} />;
  return <Dashboard />;
}

function useOrders() {
  const [data, setData] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  const load = useCallback(() => capi.get("/orders").then((r) => { setData(r.data); setUpdatedAt(new Date()); }).catch(() => {}), []);
  useEffect(() => {
    load();
    const t = setInterval(() => { if (document.visibilityState === "visible") load(); }, POLL_MS);
    return () => clearInterval(t);
  }, [load]);
  return { data, updatedAt, load };
}

function Dashboard() {
  const { customer, logout } = useCustomer();
  const nav = useNavigate();
  const { data, updatedAt, load } = useOrders();
  const [editOpen, setEditOpen] = useState(false);
  const counts = data?.counts || { pickup_drop: 0, packers_movers: 0, threepl: 0 };

  return (
    <div className="min-h-screen bg-background" data-testid="my-account-page">
      <header className="h-16 border-b border-border bg-card sticky top-0 z-20 flex items-center justify-between px-6">
        <Link to="/" data-testid="ma-home-link"><Logo className="h-9" /></Link>
        <div className="flex items-center gap-3">
          <ThemeToggle />
          <Button variant="outline" className="rounded-none gap-2 font-bold uppercase tracking-wide" data-testid="ma-logout"
            onClick={async () => { await logout(); toast.success("Signed out"); nav("/signin"); }}>
            <LogOut className="h-4 w-4" /><span className="hidden sm:inline">Logout</span>
          </Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto w-full px-6 py-10 space-y-8">
        <ProfileCard customer={customer} onEdit={() => setEditOpen(true)} />
        <div className="grid grid-cols-3 gap-3" data-testid="ma-counts">
          <CountTile icon={Package} label="Pickup & Drop" value={counts.pickup_drop} testid="ma-count-pd" />
          <CountTile icon={Home} label="Packers & Movers" value={counts.packers_movers} testid="ma-count-pm" />
          <CountTile icon={Boxes} label="3PL" value={counts.threepl} testid="ma-count-3pl" />
        </div>

        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h2 className="font-heading font-black text-2xl tracking-tight">My Orders</h2>
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1.5" data-testid="ma-live"><span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" /> Live · {updatedAt ? updatedAt.toLocaleTimeString() : "—"}</span>
            <button type="button" onClick={load} className="hover:text-primary" aria-label="Refresh" data-testid="ma-refresh"><RefreshCw className="h-4 w-4" /></button>
          </div>
        </div>

        <Tabs defaultValue="pd">
          <TabsList className="w-full grid grid-cols-3 rounded-none h-auto">
            <TabsTrigger value="pd" className="rounded-none py-2 text-xs sm:text-sm" data-testid="ma-tab-pd">Pickup &amp; Drop</TabsTrigger>
            <TabsTrigger value="pm" className="rounded-none py-2 text-xs sm:text-sm" data-testid="ma-tab-pm">Packers &amp; Movers</TabsTrigger>
            <TabsTrigger value="tpl" className="rounded-none py-2 text-xs sm:text-sm" data-testid="ma-tab-3pl">3PL Services</TabsTrigger>
          </TabsList>
          <TabsContent value="pd" className="mt-6"><PickupTab rows={data?.pickup_drop} /></TabsContent>
          <TabsContent value="pm" className="mt-6"><ListOf rows={data?.packers_movers} Card={PackersOrderCard} empty="No Packers & Movers requests yet." /></TabsContent>
          <TabsContent value="tpl" className="mt-6"><ListOf rows={data?.threepl} Card={ThreePLCard} empty="No 3PL enquiries yet." /></TabsContent>
        </Tabs>
      </main>
      <EditProfileDialog open={editOpen} onOpenChange={setEditOpen} />
    </div>
  );
}

function ProfileCard({ customer, onEdit }) {
  return (
    <section className="border border-border bg-card p-6 flex flex-col sm:flex-row sm:items-center gap-5" data-testid="ma-profile">
      <div className="h-14 w-14 shrink-0 grid place-items-center bg-primary text-primary-foreground font-heading font-black text-2xl">{(customer.name || "?").charAt(0).toUpperCase()}</div>
      <div className="flex-1 min-w-0 space-y-1">
        <div className="font-heading font-black text-2xl tracking-tight truncate" data-testid="ma-profile-name">{customer.name}</div>
        <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm text-muted-foreground">
          <span className="inline-flex items-center gap-1.5 min-w-0" data-testid="ma-profile-email"><Mail className="h-4 w-4 shrink-0" /><span className="truncate">{customer.email}</span></span>
          <span className="inline-flex items-center gap-1.5 font-mono-num" data-testid="ma-profile-mobile"><Phone className="h-4 w-4" />+91 {customer.mobile}</span>
        </div>
      </div>
      <div className="flex gap-2">
        <Button variant="outline" className="rounded-none gap-2" onClick={onEdit} data-testid="ma-edit-profile"><Pencil className="h-4 w-4" /> Edit profile</Button>
        <Button asChild className="rounded-none gap-2" data-testid="ma-new-booking"><Link to="/"><Plus className="h-4 w-4" /> New booking</Link></Button>
      </div>
    </section>
  );
}

function CountTile({ icon: Icon, label, value, testid }) {
  return (
    <div className="border border-border bg-card p-4" data-testid={testid}>
      <Icon className="h-5 w-5 text-primary" />
      <div className="font-mono-num font-black text-3xl mt-2">{value}</div>
      <div className="text-[10px] sm:text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
    </div>
  );
}

const FILTERS = [["all", "All"], ["active", "Active"], ["completed", "Completed"], ["cancelled", "Cancelled"]];

function PickupTab({ rows }) {
  const [filter, setFilter] = useState("all");
  if (!rows) return <Loading />;
  const shown = filter === "all" ? rows : rows.filter((r) => r.group === filter);
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2" data-testid="pd-filters">
        {FILTERS.map(([k, label]) => {
          const n = k === "all" ? rows.length : rows.filter((r) => r.group === k).length;
          return (
            <button key={k} type="button" onClick={() => setFilter(k)} data-testid={`pd-filter-${k}`} aria-pressed={filter === k}
              className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wide border transition-colors ${filter === k ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary"}`}>
              {label} <span className="font-mono-num opacity-70">{n}</span>
            </button>
          );
        })}
      </div>
      <ListOf rows={shown} Card={PickupOrderCard} empty={filter === "all" ? "No Pickup & Drop orders yet." : "No orders in this filter."} />
    </div>
  );
}

function ListOf({ rows, Card, empty }) {
  if (!rows) return <Loading />;
  if (rows.length === 0) {
    return (
      <div className="border border-dashed border-border p-10 text-center text-muted-foreground" data-testid="ma-empty">
        <p>{empty}</p>
        <Link to="/" className="inline-block mt-3 text-sm font-bold uppercase tracking-wide text-primary hover:underline">Book now →</Link>
      </div>
    );
  }
  return <div className="grid gap-4 md:grid-cols-2">{rows.map((o) => <Card key={o.id} o={o} />)}</div>;
}

function Loading() {
  return <div className="text-muted-foreground text-sm" data-testid="ma-loading">Loading…</div>;
}

function EditProfileDialog({ open, onOpenChange }) {
  const { customer, setCustomer } = useCustomer();
  const [f, setF] = useState({ name: "", email: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) setF({ name: customer.name || "", email: customer.email || "" });
  }, [open, customer]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await capi.put("/me", f);
      setCustomer(data);
      toast.success("Profile updated");
      onOpenChange(false);
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none" data-testid="ma-edit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><User className="h-5 w-5 text-primary" /> Edit profile</DialogTitle>
          <DialogDescription>Your mobile number is verified and can't be changed here.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div className="space-y-2"><Label htmlFor="ep-name">Full Name</Label><Input id="ep-name" className="rounded-none" value={f.name} onChange={(e) => setF((s) => ({ ...s, name: e.target.value }))} data-testid="ep-name" required /></div>
          <div className="space-y-2"><Label htmlFor="ep-email">Email Address</Label><Input id="ep-email" type="email" className="rounded-none" value={f.email} onChange={(e) => setF((s) => ({ ...s, email: e.target.value }))} data-testid="ep-email" required /></div>
          <div className="space-y-2"><Label>Mobile Number</Label><Input className="rounded-none font-mono-num" value={`+91 ${customer.mobile}`} disabled data-testid="ep-mobile" /></div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" className="rounded-none" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={busy} className="rounded-none font-bold uppercase" data-testid="ep-save">{busy ? "Saving…" : "Save"}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
